import { type RefObject, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { type DateTimePickerProps, DateTimePicker as MantineDateTimePicker } from '@mantine/dates';
import styles from '../TimePicker/TimePicker.module.sass';

export const DateTimePicker = (props: DateTimePickerProps) => {
  const { timePickerProps, valueFormat = 'MMM DD YYYY hh:mm A', ...rest } = props;
  const {
    scrollAreaProps,
    classNames: timePickerClassNames,
    popoverProps,
    ...restTimePickerProps
  } = timePickerProps ?? {};
  const listenersRef = useRef<Map<HTMLElement, () => void>>(new Map());

  useEffect(() => {
    return () => {
      listenersRef.current.forEach((cleanup) => cleanup());
      listenersRef.current.clear();
    };
  }, []);

  const mergedScrollAreaProps = useMemo(
    () => ({
      viewportRef: (node: HTMLDivElement | null) => {
        const viewportRefProp = scrollAreaProps?.viewportRef;

        if (typeof viewportRefProp === 'function') {
          viewportRefProp(node);
        } else if (viewportRefProp) {
          const ref = viewportRefProp as RefObject<HTMLDivElement | null>;
          ref.current = node;
        }

        if (node) {
          if (listenersRef.current.has(node)) return;

          const checkPosition = () => {
            const { scrollHeight, clientHeight, scrollTop } = node;
            const isAtBottom = scrollHeight - scrollTop <= clientHeight + 1;
            const root = node.closest(`.${styles.mantineTimePickerScrollarea}`);

            if (root) {
              if (isAtBottom) {
                root.setAttribute('data-at-bottom', 'true');
              } else {
                root.removeAttribute('data-at-bottom');
              }
            }
          };
          node.addEventListener('scroll', checkPosition);
          checkPosition();
          listenersRef.current.set(node, () => {
            node.removeEventListener('scroll', checkPosition);
          });
        }
      },
      ...scrollAreaProps,
    }),
    [scrollAreaProps]
  );

  const classNamesRecord = typeof timePickerClassNames === 'function' ? undefined : timePickerClassNames;

  return (
    <MantineDateTimePicker
      {...rest}
      valueFormat={valueFormat}
      timePickerProps={{
        withDropdown: true,
        format: '12h',
        popoverProps: { withinPortal: false, ...popoverProps },
        ...restTimePickerProps,
        classNames: {
          ...classNamesRecord,
          scrollarea: clsx(styles.mantineTimePickerScrollarea, classNamesRecord?.scrollarea),
        },
        scrollAreaProps: mergedScrollAreaProps,
      }}
    />
  );
};
